import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Inbox, GraduationCap, MessageSquare, SlidersHorizontal, BarChart3, Sprout, ArrowLeft, Save, Library, Download,
} from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatApiErrorDetail } from "../lib/api";
import { LibraryManager } from "../components/AdminLibrary";
import { StatusManager, EstadoBadge } from "../components/AdminStatus";

const COUNTRY_NAMES = { MX: "México", US: "Estados Unidos", ES: "España", CO: "Colombia", AR: "Argentina", ZZ: "Desconocido" };

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("rc_token", data.access_token);
      onSuccess(data.user);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-rc-bg">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-300 transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Volver al sitio
        </Link>
        <div className="p-8 md:p-10 rounded-2xl bg-rc-card border border-rc-border">
          <div className="flex items-center gap-2">
            <Sprout className="w-5 h-5 text-rc-sage" strokeWidth={1.6} />
            <span className="font-serif text-lg text-stone-100">Área del equipo</span>
          </div>
          <h1 className="mt-6 font-serif text-2xl font-light text-stone-100">Iniciar sesión</h1>
          <p className="mt-2 text-sm text-stone-500">Acceso para el equipo de Raíz Commerce.</p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-login-form">
            <div>
              <Label className="text-stone-300">Correo</Label>
              <Input type="email" required data-testid="admin-login-input-email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 bg-rc-bg2 border-rc-border text-stone-100" />
            </div>
            <div>
              <Label className="text-stone-300">Contraseña</Label>
              <Input type="password" required data-testid="admin-login-input-password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 bg-rc-bg2 border-rc-border text-stone-100" />
            </div>
            {err && <div className="text-sm text-red-400" data-testid="admin-login-error">{err}</div>}
            <button type="submit" disabled={loading} data-testid="admin-login-submit"
              className="w-full py-3 rounded-full bg-rc-forest hover:bg-rc-forestlight text-stone-100 transition-colors disabled:opacity-60">
              {loading ? "Entrando…" : "Entrar"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { id: "diagnosticos", label: "Solicitudes", icon: Inbox },
  { id: "inscripciones", label: "Inscripciones", icon: GraduationCap },
  { id: "contactos", label: "Mensajes", icon: MessageSquare },
  { id: "biblioteca", label: "Biblioteca", icon: Library },
  { id: "indicadores", label: "Indicadores", icon: SlidersHorizontal },
  { id: "metricas", label: "Visitas", icon: BarChart3 },
];

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

async function exportCsv(path, filename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("No se pudo exportar el archivo");
  }
}

const ExportBar = ({ path, filename, testid }) => (
  <div className="flex justify-end mb-4">
    <button onClick={() => exportCsv(path, filename)} data-testid={testid}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-rc-border hover:border-rc-sage/50 text-stone-200 text-sm transition-colors">
      <Download className="w-4 h-4" /> Exportar CSV
    </button>
  </div>
);

function DataTable({ cols, rows, empty, testid }) {
  if (!rows.length) return <div className="py-16 text-center text-stone-500 font-light" data-testid={`${testid}-empty`}>{empty}</div>;
  return (
    <div className="overflow-x-auto rounded-xl border border-rc-border" data-testid={testid}>
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="bg-rc-bg2">
            {cols.map((c) => <th key={c.key} className="p-4 font-mono text-xs uppercase tracking-wider text-rc-sage">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-rc-border hover:bg-rc-bg2/50 transition-colors align-top">
              {cols.map((c) => <td key={c.key} className="p-4 text-stone-300 font-light">{c.render ? c.render(r) : (r[c.key] || "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IndicadoresEditor() {
  const [form, setForm] = useState(null);

  useEffect(() => { api.get("/indicators").then((r) => setForm(r.data)); }, []);

  if (!form) return <div className="py-16 text-center text-stone-500">Cargando…</div>;

  const num = (k) => (e) => setForm((f) => ({ ...f, [k]: Number(e.target.value) }));
  const setFranja = (i, field) => (e) => setForm((f) => {
    const franjas = [...f.franjas];
    franjas[i] = { ...franjas[i], [field]: field === "cobertura_pct" ? Number(e.target.value) : e.target.value };
    return { ...f, franjas };
  });

  const save = async () => {
    try {
      await api.put("/admin/indicators", form);
      toast.success("Indicadores actualizados", { description: "Ya se reflejan en el tablero público." });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo guardar");
    }
  };

  const fields = [
    { k: "actores_convocados", label: "Actores convocados" },
    { k: "municipios_activos", label: "Municipios activos" },
    { k: "cursos_impartidos", label: "Cursos impartidos" },
    { k: "residuos_compostados_ton", label: "Residuos compostados (t)" },
    { k: "arboles_plantados", label: "Árboles plantados" },
  ];

  return (
    <div className="space-y-6" data-testid="indicadores-editor">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {fields.map((f) => (
          <div key={f.k}>
            <Label className="text-stone-300">{f.label}</Label>
            <Input type="number" step="any" data-testid={`ind-${f.k}`} value={form[f.k]} onChange={num(f.k)}
              className="mt-1.5 bg-rc-bg2 border-rc-border text-stone-100" />
          </div>
        ))}
      </div>
      <div>
        <Label className="text-stone-300">Nota del tablero</Label>
        <Input data-testid="ind-nota" value={form.nota} onChange={(e) => setForm((f) => ({ ...f, nota: e.target.value }))}
          className="mt-1.5 bg-rc-bg2 border-rc-border text-stone-100" />
      </div>
      <div>
        <div className="font-mono text-xs uppercase tracking-wider text-rc-sage mb-3">Cobertura de reforestación por franja</div>
        <div className="space-y-3">
          {form.franjas.map((fr, i) => (
            <div key={i} className="flex gap-3">
              <Input data-testid={`franja-nombre-${i}`} value={fr.nombre} onChange={setFranja(i, "nombre")}
                className="flex-1 bg-rc-bg2 border-rc-border text-stone-100" />
              <Input type="number" data-testid={`franja-pct-${i}`} value={fr.cobertura_pct} onChange={setFranja(i, "cobertura_pct")}
                className="w-28 bg-rc-bg2 border-rc-border text-stone-100" />
            </div>
          ))}
        </div>
      </div>
      <button onClick={save} data-testid="indicadores-save-btn"
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-rc-forest hover:bg-rc-forestlight text-stone-100 transition-colors">
        <Save className="w-4 h-4" /> Guardar cambios
      </button>
    </div>
  );
}

function Metricas() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/admin/metrics").then((r) => setM(r.data)); }, []);
  if (!m) return <div className="py-16 text-center text-stone-500">Cargando…</div>;

  return (
    <div className="space-y-8" data-testid="metricas-panel">
      <div className="p-6 rounded-2xl bg-rc-card border border-rc-border inline-block">
        <div className="text-xs font-mono uppercase tracking-wider text-stone-500">Visitas totales</div>
        <div className="mt-2 font-serif text-4xl font-light text-stone-100">{m.total.toLocaleString("es-MX")}</div>
      </div>

      <div className="p-6 rounded-2xl bg-rc-card border border-rc-border">
        <div className="text-xs font-mono uppercase tracking-wider text-rc-sage">Visitas por día (últimas 2 semanas)</div>
        <div className="mt-6 h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={m.by_day}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#82b385" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#82b385" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#252d28" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "#68706b", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#68706b", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#181d1a", border: "1px solid #252d28", borderRadius: 8, color: "#f3f4f2" }} />
              <Area type="monotone" dataKey="count" stroke="#82b385" strokeWidth={2} fill="url(#g)" name="Visitas" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="p-6 rounded-2xl bg-rc-card border border-rc-border">
          <div className="text-xs font-mono uppercase tracking-wider text-rc-sage">Origen aproximado (país)</div>
          <div className="mt-4 space-y-2">
            {m.by_country.length === 0 && <div className="text-stone-500 text-sm">Aún sin datos</div>}
            {m.by_country.map((c) => (
              <div key={c.label} className="flex justify-between items-center text-sm">
                <span className="text-stone-300">{COUNTRY_NAMES[c.label] || c.label}</span>
                <span className="font-mono text-stone-400">{c.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="p-6 rounded-2xl bg-rc-card border border-rc-border">
          <div className="text-xs font-mono uppercase tracking-wider text-rc-sage">Secciones más vistas</div>
          <div className="mt-4 space-y-2">
            {m.by_section.length === 0 && <div className="text-stone-500 text-sm">Aún sin datos</div>}
            {m.by_section.map((s) => (
              <div key={s.label} className="flex justify-between items-center text-sm">
                <span className="text-stone-300">{s.label}</span>
                <span className="font-mono text-stone-400">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Panel({ user, onLogout }) {
  const [tab, setTab] = useState("diagnosticos");
  const [diag, setDiag] = useState([]);
  const [insc, setInsc] = useState([]);
  const [cont, setCont] = useState([]);

  const load = useCallback(async () => {
    try {
      const [d, i, c] = await Promise.all([
        api.get("/admin/diagnosticos"), api.get("/admin/inscripciones"), api.get("/admin/contactos"),
      ]);
      setDiag(d.data); setInsc(i.data); setCont(c.data);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-rc-bg">
      <header className="border-b border-rc-border bg-rc-bg2/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sprout className="w-5 h-5 text-rc-sage" strokeWidth={1.6} />
            <span className="font-serif text-stone-100">Raíz Commerce · Equipo</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-stone-500 hidden sm:block">{user.name}</span>
            <button onClick={onLogout} data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 text-sm text-stone-400 hover:text-red-400 transition-colors">
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm transition-colors ${
                tab === t.id ? "bg-rc-forest text-stone-100" : "bg-rc-card text-stone-400 hover:text-stone-200 border border-rc-border"
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "diagnosticos" && (
          <>
            <ExportBar path="/admin/diagnosticos.csv" filename="diagnosticos.csv" testid="export-diagnosticos-btn" />
            <DataTable testid="table-diagnosticos" empty="Aún no hay solicitudes de diagnóstico."
            rows={diag}
            cols={[
              { key: "created_at", label: "Fecha", render: (r) => fmtDate(r.created_at) },
              { key: "estado", label: "Estado", render: (r) => <EstadoBadge estado={r.estado} /> },
              { key: "nombre", label: "Nombre" },
              { key: "correo", label: "Correo" },
              { key: "organizacion", label: "Organización" },
              { key: "vertiente", label: "Vertiente" },
              { key: "notas", label: "Notas" },
              { key: "acciones", label: "", render: (r) => <StatusManager collection="diagnosticos" row={r} onSaved={load} /> },
            ]} />
          </>
        )}
        {tab === "inscripciones" && (
          <>
            <ExportBar path="/admin/inscripciones.csv" filename="inscripciones.csv" testid="export-inscripciones-btn" />
            <DataTable testid="table-inscripciones" empty="Aún no hay inscripciones."
            rows={insc}
            cols={[
              { key: "created_at", label: "Fecha", render: (r) => fmtDate(r.created_at) },
              { key: "estado", label: "Estado", render: (r) => <EstadoBadge estado={r.estado} /> },
              { key: "curso_titulo", label: "Curso" },
              { key: "nombre", label: "Nombre" },
              { key: "correo", label: "Correo" },
              { key: "telefono", label: "Teléfono" },
              { key: "notas", label: "Notas" },
              { key: "acciones", label: "", render: (r) => <StatusManager collection="inscripciones" row={r} onSaved={load} /> },
            ]} />
          </>
        )}
        {tab === "contactos" && (
          <DataTable testid="table-contactos" empty="Aún no hay mensajes de contacto."
            rows={cont}
            cols={[
              { key: "created_at", label: "Fecha", render: (r) => fmtDate(r.created_at) },
              { key: "estado", label: "Estado", render: (r) => <EstadoBadge estado={r.estado} /> },
              { key: "nombre", label: "Nombre" },
              { key: "correo", label: "Correo" },
              { key: "mensaje", label: "Mensaje" },
              { key: "notas", label: "Notas" },
              { key: "acciones", label: "", render: (r) => <StatusManager collection="contactos" row={r} onSaved={load} /> },
            ]} />
        )}
        {tab === "biblioteca" && <LibraryManager />}
        {tab === "indicadores" && <IndicadoresEditor />}
        {tab === "metricas" && <Metricas />}
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("rc_token");
    if (!token) { setChecking(false); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => localStorage.removeItem("rc_token")).finally(() => setChecking(false));
  }, []);

  const logout = () => { localStorage.removeItem("rc_token"); setUser(null); };

  if (checking) return <div className="min-h-screen bg-rc-bg flex items-center justify-center text-stone-500">Cargando…</div>;
  if (!user) return <Login onSuccess={setUser} />;
  return <Panel user={user} onLogout={logout} />;
}
